<script setup>
import ImgCatPractice from '@manseImages/yongsin-light/img-yongsin-cat-practice.png';
import ImgDotsConnector from '@manseImages/yongsin-light/img-yongsin-dots-connector.svg?url';

const fullWidthMode = inject('fullWidthMode', false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));

const props = defineProps({
  isMobile: Boolean,
  contentData: {
    type: Object,
    default: null,
  },
  activeSide: {
    type: String,
    default: null, // 'left' | 'right' (모바일 전용)
  },
});

// ═══ 왼쪽 페이지 데이터 (기존 오른쪽 → 왼쪽으로 이동) ═══

// ── 균형(내향/외향) 섹션 ──
const balanceLabel = computed(() => props.contentData?.balanceLabel ?? '');
const balanceDesc = computed(() => props.contentData?.balanceDesc ?? '');
const introvertRatio = computed(() => props.contentData?.introvertRatio ?? 0);
const extrovertRatio = computed(() => props.contentData?.extrovertRatio ?? 0);
const introvertLabel = computed(() => props.contentData?.introvertLabel ?? '');
const extrovertLabel = computed(() => props.contentData?.extrovertLabel ?? '');
const balanceSummaryTitle = computed(() => props.contentData?.balanceSummaryTitle ?? '');
const balanceSummaryLines = computed(() => props.contentData?.balanceSummaryLines ?? []);

// 도넛 차트 중심 라벨 텍스트
const balanceCenterLabel = computed(() => {
  if (introvertRatio.value > extrovertRatio.value) return '내향';
  if (extrovertRatio.value > introvertRatio.value) return '외향';
  return '균형';
});

// ── 조후(뜨거운/차가운 기운) 섹션 ──
const johuLabel = computed(() => props.contentData?.johuLabel ?? '');
const johuDesc = computed(() => props.contentData?.johuDesc ?? '');
const hotRatio = computed(() => props.contentData?.hotRatio ?? 0);
const coldRatio = computed(() => props.contentData?.coldRatio ?? 0);
const johuSummaryTitle = computed(() => props.contentData?.johuSummaryTitle ?? '');
const johuSummaryLines = computed(() => props.contentData?.johuSummaryLines ?? []);

const johuCenterLabel = computed(() => '조후');

// ═══ 오른쪽 페이지 데이터 (에너지타입) ═══
const strengths = computed(() => props.contentData?.strengths ?? null);
const growthPoints = computed(() => props.contentData?.growthPoints ?? null);
const actionGuide = computed(() => props.contentData?.actionGuide ?? null);

const strengthLines = computed(() =>
  strengths.value?.설명 ? strengths.value.설명.split('\n') : [],
);
const strengthTags = computed(() => strengths.value?.태그 ?? []);

const growthLines = computed(() =>
  growthPoints.value?.설명 ? growthPoints.value.설명.split('\n') : [],
);
const growthTags = computed(() => growthPoints.value?.태그 ?? []);

const practiceLines = computed(() =>
  actionGuide.value?.내용 ? actionGuide.value.내용.split('\n') : [],
);
const practiceHighlightLines = computed(() =>
  actionGuide.value?.마무리 ? actionGuide.value.마무리.split('\n') : [],
);

// ═══ 그래프 애니메이션 ═══
const animatedLeft = ref(false);
const chartSectionRef = ref(null);

// 모바일: activeSide prop 변경 시 애니메이션 트리거
watch(
  () => props.activeSide,
  (side) => {
    if (!props.isMobile) return;
    if (side === 'left') {
      animatedLeft.value = false;
      nextTick(() =>
        setTimeout(() => {
          animatedLeft.value = true;
        }, 100),
      );
    }
  },
);

onMounted(() => {
  if (props.isMobile) {
    if (props.activeSide === 'left') {
      setTimeout(() => {
        animatedLeft.value = true;
      }, 100);
    }
    return;
  }

  // PC: IntersectionObserver로 트리거
  if (!chartSectionRef.value) {
    animatedLeft.value = true;
    return;
  }

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          animatedLeft.value = true;
        }, 100);
        observer.disconnect();
      }
    },
    { threshold: 0.2 },
  );
  observer.observe(chartSectionRef.value);
});
</script>

<template>
  <VRow class="full-height">
    <!-- ═══ 왼쪽 페이지 (균형/조후 도넛차트) ═══ -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="ysl-card full-height right">
        <!-- 타이틀 바 -->
        <div class="ysl-title-bar">
          <span class="ysl-title-bar__num">4</span>
          <span class="ysl-title-bar__text">나는 어떤 타입일까?</span>
        </div>

        <!-- 본문 영역 -->
        <div class="ysl-content">
          <div ref="chartSectionRef" class="ysl04-left-body">
            <!-- ── 1) 나는 어떤 쪽에 더 가까울까? ── -->
            <div class="ysl04-section">
              <!-- 헤더: 제목 + 뱃지 -->
              <div class="ysl04-header">
                <p class="ysl04-header__title">
                  <span class="ysl04-header__emoji">⚖</span> 나는 어떤 쪽에 더 가까울까?
                </p>
                <span class="ysl04-badge">{{ balanceLabel }}</span>
              </div>
              <p class="ysl04-desc">{{ balanceDesc }}</p>

              <!-- 도넛 차트 + 범례 -->
              <div class="ysl04-donut-area">
                <div class="ysl04-donut-wrap">
                  <svg class="ysl04-donut" viewBox="0 0 120 120">
                    <circle
                      class="ysl04-donut__segment"
                      cx="60"
                      cy="60"
                      r="44"
                      fill="none"
                      stroke-width="24"
                      stroke="#FFD374"
                      :stroke-dasharray="
                        animatedLeft ? `${(introvertRatio / 100) * 276.46} 276.46` : '0 276.46'
                      "
                      stroke-dashoffset="0"
                      transform="rotate(-90 60 60)"
                    />
                    <circle
                      class="ysl04-donut__segment"
                      cx="60"
                      cy="60"
                      r="44"
                      fill="none"
                      stroke-width="24"
                      stroke="#C2C2C2"
                      :stroke-dasharray="
                        animatedLeft ? `${(extrovertRatio / 100) * 276.46} 276.46` : '0 276.46'
                      "
                      :stroke-dashoffset="
                        animatedLeft ? `${-(introvertRatio / 100) * 276.46}` : '0'
                      "
                      transform="rotate(-90 60 60)"
                    />
                  </svg>
                  <span class="ysl04-donut__center-label">{{ balanceCenterLabel }}</span>
                </div>

                <div class="ysl04-legend">
                  <div class="ysl04-legend-row">
                    <span class="ysl04-legend-dot" style="background: #ffd374" />
                    <span class="ysl04-legend-name">내향</span>
                    <span class="ysl04-legend-value">{{ introvertLabel }}</span>
                  </div>
                  <div class="ysl04-legend-row">
                    <span class="ysl04-legend-dot" style="background: #c2c2c2" />
                    <span class="ysl04-legend-name">외향</span>
                    <span class="ysl04-legend-value">{{ extrovertLabel }}</span>
                  </div>
                </div>
              </div>

              <!-- 요약 카드 -->
              <div class="ysl04-summary-card">
                <p class="ysl04-summary-card__title">{{ balanceSummaryTitle }}</p>
                <div class="ysl04-summary-card__divider" />
                <div class="ysl04-summary-card__text">
                  <p v-for="(line, idx) in balanceSummaryLines" :key="idx">{{ line }}</p>
                </div>
              </div>
            </div>

            <!-- ── 2) 내 기운은 따뜻할까, 차가울까? ── -->
            <div class="ysl04-section">
              <!-- 헤더: 제목 + 뱃지 -->
              <div class="ysl04-header">
                <p class="ysl04-header__title">
                  <span class="ysl04-header__emoji">🌡</span> 내 기운은 따뜻할까, 차가울까?
                </p>
                <span class="ysl04-badge">{{ johuLabel }}</span>
              </div>
              <p class="ysl04-desc">{{ johuDesc }}</p>

              <!-- 도넛 차트 + 범례 -->
              <div class="ysl04-donut-area">
                <div class="ysl04-donut-wrap">
                  <svg class="ysl04-donut" viewBox="0 0 120 120">
                    <circle
                      class="ysl04-donut__segment"
                      cx="60"
                      cy="60"
                      r="44"
                      fill="none"
                      stroke-width="24"
                      stroke="#FFD374"
                      :stroke-dasharray="
                        animatedLeft ? `${(hotRatio / 100) * 276.46} 276.46` : '0 276.46'
                      "
                      stroke-dashoffset="0"
                      transform="rotate(-90 60 60)"
                    />
                    <circle
                      class="ysl04-donut__segment"
                      cx="60"
                      cy="60"
                      r="44"
                      fill="none"
                      stroke-width="24"
                      stroke="#73A1F8"
                      :stroke-dasharray="
                        animatedLeft ? `${(coldRatio / 100) * 276.46} 276.46` : '0 276.46'
                      "
                      :stroke-dashoffset="animatedLeft ? `${-(hotRatio / 100) * 276.46}` : '0'"
                      transform="rotate(-90 60 60)"
                    />
                  </svg>
                  <span class="ysl04-donut__center-label">{{ johuCenterLabel }}</span>
                </div>

                <div class="ysl04-legend">
                  <div class="ysl04-legend-row">
                    <span class="ysl04-legend-dot" style="background: #ffd374" />
                    <span class="ysl04-legend-name">뜨거운 기운</span>
                    <span class="ysl04-legend-value">{{ hotRatio }}%</span>
                  </div>
                  <div class="ysl04-legend-row">
                    <span class="ysl04-legend-dot" style="background: #73a1f8" />
                    <span class="ysl04-legend-name">차가운 기운</span>
                    <span class="ysl04-legend-value">{{ coldRatio }}%</span>
                  </div>
                </div>
              </div>

              <!-- 요약 카드 -->
              <div class="ysl04-summary-card">
                <p class="ysl04-summary-card__title">{{ johuSummaryTitle }}</p>
                <div class="ysl04-summary-card__divider" />
                <div class="ysl04-summary-card__text">
                  <p v-for="(line, idx) in johuSummaryLines" :key="idx">{{ line }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>

    <!-- ═══ 오른쪽 페이지 (비어 있음 — 추후 콘텐츠 추가 예정) ═══ -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="ysl-card full-height">
        <div class="ysl-title-bar" :class="{ 'ysl-title-bar--text-hidden': !isMobile }">
          <span class="ysl-title-bar__num">4</span>
          <span class="ysl-title-bar__text">나는 어떤 타입일까?</span>
        </div>

        <div class="ysl-content">
          <div class="ysl04-right-body">
            <!-- ── 1) 강점 / 성장 포인트 카드 ── -->
            <div class="ysl04-cards-row">
              <!-- 강점 카드 -->
              <div class="ysl04-trait-card ysl04-trait-card--strength">
                <p class="ysl04-trait-card__title">강점</p>
                <div class="ysl04-trait-card__desc">
                  <p v-for="(line, i) in strengthLines" :key="'sl' + i">{{ line }}</p>
                </div>
                <div class="ysl04-trait-card__keywords">
                  <span
                    v-for="(tag, i) in strengthTags"
                    :key="'st' + i"
                    class="ysl04-keyword ysl04-keyword--strength"
                  >{{ tag }}</span>
                </div>
              </div>

              <!-- 성장 포인트 카드 -->
              <div class="ysl04-trait-card ysl04-trait-card--growth">
                <p class="ysl04-trait-card__title">성장 포인트</p>
                <div class="ysl04-trait-card__desc">
                  <p v-for="(line, i) in growthLines" :key="'gl' + i">{{ line }}</p>
                </div>
                <div class="ysl04-trait-card__keywords">
                  <span
                    v-for="(tag, i) in growthTags"
                    :key="'gt' + i"
                    class="ysl04-keyword ysl04-keyword--growth"
                  >{{ tag }}</span>
                </div>
              </div>
            </div>

            <!-- ── 2) 점 연결선 + 고양이 일러스트 + 실천 안내 카드 ── -->
            <div class="ysl04-bottom-area">
              <img :src="ImgDotsConnector" alt="" class="ysl04-dots" />
              <img :src="ImgCatPractice" alt="" class="ysl04-cat-img" />
              <div class="ysl04-practice-card">
                <div class="ysl04-practice-card__text">
                  <p v-for="(line, i) in practiceLines" :key="'pl' + i">{{ line }}</p>
                </div>
                <div class="ysl04-practice-card__highlight">
                  <p v-for="(line, i) in practiceHighlightLines" :key="'ph' + i">{{ line }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
$ysl-primary: #4e80d6;
$ysl-primary-light: rgba(78, 128, 214, 0.05);
$ysl-text: #333;

// ── 공통 (content1/2/3과 동일 구조) ──
.ysl-card {
  background: #fff;
  border-radius: 0 !important;
  box-shadow: none !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.right {
    border-right: 1px solid #ebebeb;
  }
}

.ysl-title-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 24px;
  background: $ysl-primary-light;
  flex-shrink: 0;

  &--text-hidden {
    .ysl-title-bar__num,
    .ysl-title-bar__text {
      visibility: hidden;
    }
  }

  &__num {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 11px;
    background: #5d81bf;
    color: #fff;
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    flex-shrink: 0;
  }

  &__text {
    color: #5d81bf;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }
}

.ysl-content {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 12px 24px;
  padding-top: 48px;
  padding-bottom: 24px;
  display: flex;
  flex-direction: column;
}

// ═══════════════════════════════
// content4 왼쪽 바디 (균형/조후)
// ═══════════════════════════════
.ysl04-left-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  justify-content: space-between;
}

.ysl04-section {
  background: #fff;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-inline-start: 12px;
  }
}

// ── 헤더 (제목 + 뱃지) ──
.ysl04-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-inline-start: 0;
  gap: 8px;

  &__title {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    color: $ysl-text;
    margin: 0;
    white-space: nowrap;
  }

  &__emoji {
    font-size: 24px;
    vertical-align: middle;
    line-height: 1;
    position: relative;
    top: -3px;
  }
}

.ysl04-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 2px 8px;
  border-radius: 40px;
  background: rgba(94, 159, 28, 0.1);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  color: #5e9f1c;
  white-space: nowrap;
}

.ysl04-desc {
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  color: #575757;
  margin: 0;
  width: 100%;
}

// ── 도넛 차트 영역 ──
.ysl04-donut-area {
  display: flex;
  align-items: center;
  gap: 30px;
}

.ysl04-donut-wrap {
  position: relative;
  width: 112px;
  height: 112px;
  flex-shrink: 0;
}

.ysl04-donut {
  width: 100%;
  height: 100%;
}

.ysl04-donut__segment {
  transition:
    stroke-dasharray 1.2s cubic-bezier(0.22, 1, 0.36, 1),
    stroke-dashoffset 1.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.ysl04-donut__center-label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  color: $ysl-text;
  pointer-events: none;
}

// ── 범례 ──
.ysl04-legend {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ysl04-legend-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ysl04-legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ysl04-legend-name {
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: $ysl-text;
  min-width: 70px;
}

.ysl04-legend-value {
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: $ysl-text;
}

// ── 요약 카드 ──
.ysl04-summary-card {
  width: 100%;
  background: #f8f8f8;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  &__title {
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.5;
    color: $ysl-text;
    text-align: center;
    margin: 0;
  }

  &__divider {
    width: 30px;
    height: 1px;
    background: #c2c2c2;
  }

  &__text {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;
    text-align: center;

    p {
      margin: 0;
    }
  }
}

// ═══════════════════════════════
// content4 오른쪽 페이지
// ═══════════════════════════════
.ysl04-right-body {
  display: flex;
  flex-direction: column;
  gap: 60px;
  align-items: center;
  flex: 1;
}

// ── 강점 / 성장 포인트 카드 행 ──
.ysl04-cards-row {
  display: flex;
  gap: 16px;
  align-items: stretch;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  container-type: inline-size;
}

.ysl04-trait-card {
  flex: 1 1 220px;
  min-width: 0;
  max-width: 268px;

  @container (max-width: 455px) {
    max-width: 100%;
    flex-basis: 100%;
  }
  background: #fff;
  border-top: 2px solid;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;

  &--strength {
    border-top-color: #db9823;
  }

  &--growth {
    border-top-color: #949494;
  }

  &__title {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    margin: 0;
    white-space: nowrap;

    .ysl04-trait-card--strength & {
      color: #db9823;
    }

    .ysl04-trait-card--growth & {
      color: #949494;
    }
  }

  &__desc {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: $ysl-text;

    p {
      margin: 0;
    }
  }

  &__keywords {
    display: flex;
    gap: 8px;
    align-items: center;
  }
}

.ysl04-keyword {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 6px;
  border-radius: 4px;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
  white-space: nowrap;

  &--strength {
    background: rgba(219, 152, 35, 0.07);
    color: #db9823;
  }

  &--growth {
    background: rgba(148, 148, 148, 0.07);
    color: #949494;
  }
}

// ── 하단 영역 (점 + 고양이 + 실천 카드) ──
.ysl04-bottom-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  min-width: 0;
}

.ysl04-dots {
  width: 8px;
  height: 48px;
}

.ysl04-cat-img {
  width: 122px;
  height: 124px;
  object-fit: contain;
  transform: scaleY(-1) rotate(180deg);
  position: relative;
  z-index: 1;
  margin-top: 20px;
  margin-bottom: -44px;
}

// ── 실천 안내 카드 ──
.ysl04-practice-card {
  width: 552px;
  max-width: 100%;
  background: #fcf8f0;
  border: 1px solid #db9823;
  border-radius: 20px;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  padding: 40px 26px;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  text-align: center;

  &__text {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: $ysl-text;

    p {
      margin: 0;
    }
  }

  &__highlight {
    font-family: Pretendard;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.5;
    color: #db9823;

    p {
      margin: 0;
    }
  }
}
</style>
